import {
  Autocomplete,
  AutocompleteChangeReason,
  CircularProgress,
  InputAdornment,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import React, { KeyboardEvent, useEffect } from "react";
import style from "./styles";
import { FieldRenderProps } from "react-final-form";

export interface OptionType {
  value: string;
  label: string;
}
interface Props {
  onChange(id: string, value?: OptionType): void;
  onInputChange(id: string, value?: string): void;
  input: any; // value / ref / onChange / onBlur
  label?: string;
  placeholder?: string;
  fetchOptions?(param: { nom?: string; codePostal?: string }): void;
  getOptions?: OptionType[];
  loading?: boolean;
  errors?: any;
  disabled?: boolean;
  disableClearable?: boolean;
  searchIcon?: boolean;
  variant?: "primary" | "secondary";
  onClickEndornment?(): void;
  maxLength?: number;
  horizontal?: boolean;
  required?: boolean;
  enableKeyDown?: boolean;
}
function InputSearch(props: Props & FieldRenderProps<any, HTMLElement, any>) {
  const {
    label,
    fetchOptions,
    getOptions,
    loading,
    placeholder,
    errors,
    disabled,
    disableClearable,
    searchIcon,
    variant = "primary",
    onClickEndornment,
    input,
    maxLength,
    horizontal,
    required,
    enableKeyDown,
  } = props;
  const [inputValue, setInputValue] = React.useState(
    (input.value && input.value.value) || undefined,
  );
  const [stateValue, setStateValue] = React.useState(input.value || null);
  const [options, setOptions] = React.useState<OptionType[]>(getOptions || []);
  const [isDirtyRequired, setDirtyRequired] = React.useState<boolean>(false);

  const onChange = (
    e: React.ChangeEvent<{}>,
    newValue: OptionType | null,
    reason: AutocompleteChangeReason,
  ) => {
    e.preventDefault();
    setStateValue(newValue);
    if (getOptions) {
      const optionsSelected = getOptions.find(
        (opt) => opt.value && newValue && opt.value === newValue.value,
      );
      input.onChange(optionsSelected);
    }
    if (!newValue && required) {
      setDirtyRequired(true);
    } else {
      setDirtyRequired(false);
    }
    if (reason === "clear") {
      setStateValue(undefined);
      setInputValue(undefined);
      if (input.onChange) {
        input.onChange(undefined);
        // props.onInputChange(input.name, undefined);
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && onClickEndornment) {
      onClickEndornment();
    }
  };

  const onInputChange = (e: React.ChangeEvent<{}>, newValue: any) => {
    e && e.preventDefault();
    setInputValue(newValue);
    // props.onInputChange(input.name, newValue);
    if (fetchOptions && newValue.length > 1) {
      const fetchValue = isNaN(newValue)
        ? { nom: newValue }
        : { codePostal: newValue };
      fetchOptions(fetchValue);
    } else {
      setOptions([]);
    }
    const value = getOptions;

    if (value) {
      setOptions(value);
      const optionsSelected = value.find(
        (opt) => opt.value && newValue && opt.value === newValue.toUpperCase(),
      );
      optionsSelected && input.onChange(optionsSelected);
    } else {
      setOptions([]);
    }

    if (!newValue) {
      if (input.onChange) {
        input.onChange(undefined);
        //     props.onInputChange(input.name, undefined);
      }
    }
  };

  const handleBlur = (e: any) => {
    if (!e.target.value && required) {
      setDirtyRequired(true);
    } else {
      setDirtyRequired(false);
    }
  };
  useEffect(() => {
    if (!stateValue) {
      setOptions([]);
    }

    setOptions(getOptions || []);
  }, [getOptions]);

  useEffect(() => {
    if (input.value) {
      setStateValue(input.value);
      setInputValue(input.value.value);
    } else {
      setStateValue(null);
      setInputValue(null);
    }
  }, [input.value && input.value.value]);

  const requiredAsterisk = input.required ? " *" : "";

  let className = "";
  if (horizontal) className += " autocomplete-horizontal";
  if (searchIcon) className += " autocomplete-searchIcon";
  return (
    <>
      <Autocomplete
        sx={style}
        className={className}
        disabled={disabled}
        id={input.name}
        disablePortal
        getOptionLabel={(option) => option.label}
        loading={loading || false}
        loadingText={"Chargement..."}
        options={options}
        value={stateValue}
        openOnFocus={false}
        isOptionEqualToValue={(option, value) =>
          value && option.value === value
        }
        onKeyDown={enableKeyDown ? handleKeyDown : undefined}
        disableClearable={disableClearable}
        filterOptions={(options, state) => options}
        onChange={(event, newValue, reason) => {
          onChange(event, newValue, reason);
        }}
        PopperComponent={(props) => (
          <Popper
            {...props}
            popperOptions={{
              placement: "bottom",
            }}
          />
        )}
        onBlur={handleBlur}
        forcePopupIcon={false}
        onInputChange={(event, newValue) => {
          onInputChange(event, newValue);
        }}
        // TODO traduction
        noOptionsText={!stateValue ? "Choisir " : "Pas de suggestions"}
        renderInput={(params) => (
          <TextField
            name={input.name}
            inputRef={input.ref}
            className={variant === "primary" ? "inputSearch-primary" : ""}
            {...params}
            placeholder={placeholder}
            required={required}
            autoComplete="off"
            error={isDirtyRequired || errors ? true : false}
            InputProps={{
              ...params.InputProps,
              classes: {
                input: "textField",
                underline: "underline",
              },
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={10} /> : null}
                  {searchIcon ? (
                    <InputAdornment position="end" onClick={onClickEndornment}>
                      dropdown
                    </InputAdornment>
                  ) : (
                    params.InputProps.endAdornment
                  )}
                </>
              ),
            }}
            inputProps={{ ...params.inputProps, maxLength: maxLength }}
            InputLabelProps={{
              classes: {
                root: "textFieldLabel",
                focused: "textFieldLabelFocused",
                error: "textFieldLabelError",
                asterisk: "textFieldLabelAsterisk",
              },
            }}
            label={label ? label + requiredAsterisk : ""}
          />
        )}
      />
      {/* {errors && errors.type === "required" && (
        <Typography color="error" className="inputField-error">
          {t("error.requiredInput")}
        </Typography>
      )} */}
    </>
  );
}

export default InputSearch;
